import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import StepIndicator from './StepIndicator';
import { startFlow, track } from './analytics';
import './UploadSteps.css';

// One status at a time — the ONLY message in the overlay, advancing through the
// real phases in order (the file uploads, then it's read and the details
// extracted). No separate heading competes with it. Nothing is saved as a bill
// here; that only happens at the final step.
const PROCESSING_MESSAGES = [
    'Uploading your document…',
    'Reading your document…',
    'Finding services and prices…',
    'Organizing the details for your review…',
    'Almost there — this can take up to a minute…'
];

const MAX_PAGES = 15;
let pageIdSeq = 0;

// Trash icon for "remove this page" — clearer than an ✕, which testers read as
// an error state rather than a delete action.
const TrashIcon = () => (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 6h18" />
        <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
        <path d="M6 6l1 14a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-14" />
        <path d="M10 11v6M14 11v6" />
    </svg>
);

const Step1Upload = ({ setImage }) => {
    // pages: an ordered list of the document's pages. Either a single PDF, or
    // one-or-more images (a multi-page paper bill photographed page by page).
    const [pages, setPages] = useState([]); // { id, file, url, kind: 'image'|'pdf' }
    const [loading, setLoading] = useState(false);
    const [progressMessage, setProgressMessage] = useState(PROCESSING_MESSAGES[0]);
    const abortRef = useRef(null);
    const addFileInputRef = useRef(null);
    const navigate = useNavigate();

    const isPdf = pages.length === 1 && pages[0].kind === 'pdf';
    const imageCount = pages.filter(p => p.kind === 'image').length;

    // Cycle the reassurance messages while processing.
    useEffect(() => {
        if (!loading) {
            setProgressMessage(PROCESSING_MESSAGES[0]);
            return undefined;
        }
        let index = 0;
        const timer = setInterval(() => {
            index = Math.min(index + 1, PROCESSING_MESSAGES.length - 1);
            setProgressMessage(PROCESSING_MESSAGES[index]);
        }, 12000);
        return () => clearInterval(timer);
    }, [loading]);

    // Abort in-flight processing + release preview object URLs on unmount.
    useEffect(() => () => {
        if (abortRef.current) abortRef.current.abort();
    }, []);
    useEffect(() => () => {
        pages.forEach(p => { if (p.url) URL.revokeObjectURL(p.url); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const makePage = (file) => ({
        id: ++pageIdSeq,
        file,
        kind: file.type === 'application/pdf' ? 'pdf' : 'image',
        url: file.type === 'application/pdf' ? null : URL.createObjectURL(file)
    });

    // Add selected files. A PDF is a standalone document (replaces everything);
    // images accumulate as pages.
    const addFiles = (fileList) => {
        const files = Array.from(fileList || []);
        if (!files.length) return;

        // Begin a new upload attempt the first time a file is added, so the
        // funnel can measure drop-off from this point onward.
        if (pages.length === 0) {
            startFlow();
            const firstKind = files[0].type === 'application/pdf' ? 'pdf' : 'image';
            track('upload_started', { fileKind: firstKind });
        }

        const pdf = files.find(f => f.type === 'application/pdf');
        if (pdf) {
            setPages(prev => {
                prev.forEach(p => { if (p.url) URL.revokeObjectURL(p.url); });
                return [makePage(pdf)];
            });
            return;
        }

        const images = files.filter(f => f.type.startsWith('image/'));
        if (!images.length) return;
        setPages(prev => {
            const base = prev.filter(p => p.kind === 'image'); // drop a prior PDF if switching to photos
            const room = Math.max(0, MAX_PAGES - base.length);
            return [...base, ...images.slice(0, room).map(makePage)];
        });
    };

    const handleFileInputChange = (e) => {
        addFiles(e.target.files);
        e.target.value = ''; // allow re-selecting the same file
    };

    // Capture a photo from the camera and append it as a page.
    const handleCameraCapture = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.capture = 'environment';
        input.onchange = (e) => addFiles(e.target.files);
        input.click();
    };

    const removePage = (id) => {
        setPages(prev => {
            const target = prev.find(p => p.id === id);
            if (target?.url) URL.revokeObjectURL(target.url);
            return prev.filter(p => p.id !== id);
        });
    };

    const clearAll = () => {
        setPages(prev => { prev.forEach(p => { if (p.url) URL.revokeObjectURL(p.url); }); return []; });
    };

    const handleCancelProcessing = () => {
        if (abortRef.current) abortRef.current.abort();
        setLoading(false);
    };

    const handleCancelUpload = () => {
        if (abortRef.current) abortRef.current.abort();
        navigate('/dashboard');
    };

    const handleNext = async () => {
        if (!pages.length) {
            alert('Please add a bill or estimate to upload.');
            return;
        }

        setLoading(true);
        track('process_started', { pageCount: pages.length, fileKind: isPdf ? 'pdf' : 'image' });
        const controller = new AbortController();
        abortRef.current = controller;
        const formData = new FormData();
        pages.forEach(p => formData.append('file', p.file));

        try {
            const response = await fetch('/process', {
                method: 'POST',
                body: formData,
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                signal: controller.signal,
            });

            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                if (response.status === 401) {
                    setLoading(false);
                    localStorage.removeItem('user');
                    localStorage.removeItem('token');
                    alert('Your login session expired. Please log in again before uploading.');
                    navigate('/login', { state: { from: { pathname: '/upload/step1' } } });
                    return;
                }
                throw new Error(data.message || 'There was an error processing your file. Please try again.');
            }

            const parsedData = typeof data === 'string' ? JSON.parse(data) : data;
            setImage(parsedData);
            track('process_succeeded', {
                serviceCount: Array.isArray(parsedData?.services) ? parsedData.services.length : 0,
                documentType: parsedData?.documentType
            });
            setLoading(false);
            navigate('/upload/step2', { state: { ocrData: parsedData } });
        } catch (error) {
            if (error.name === 'AbortError') {
                track('process_canceled');
                return;
            }
            console.error('Error processing file:', error);
            track('process_failed', { errorCategory: error.message ? String(error.message).slice(0, 40) : 'unknown' });
            setLoading(false);
            alert(error.message || 'There was an error processing your file. Please try again.');
        } finally {
            abortRef.current = null;
        }
    };

    const hasPages = pages.length > 0;

    return (
        <div className="upload-steps-container step-one-container">
            <StepIndicator currentStep={1} />
            <div className="step-one-header">
                <div>
                    <h2>Upload a vet bill or estimate</h2>
                    <p>You'll review and edit everything before it becomes part of VetBill's public comparison data.</p>
                </div>
            </div>

            <div className="step-one-upload-panel">
                <div className="privacy-notice">
                    <strong>What to upload</strong>
                    <p>Use a clear bill, invoice, estimate, treatment plan, screenshot, or PDF. For a paper bill with multiple pages, photograph each page — they’ll be saved as one document. It’s okay if we don’t read it perfectly; you can fix anything in the next step.</p>
                </div>

                <div className="upload-source-row">
                    <button type="button" className="capture-btn" onClick={handleCameraCapture}>
                        {imageCount > 0 ? 'Add photo' : 'Capture photo'}
                    </button>
                    <button type="button" className="file-btn" onClick={() => addFileInputRef.current?.click()}>
                        {isPdf ? 'Replace file' : (hasPages ? 'Add file(s)' : 'Choose file(s)')}
                    </button>
                    <input
                        ref={addFileInputRef}
                        type="file"
                        id="fileInput"
                        accept="image/*,.pdf,application/pdf"
                        multiple
                        onChange={handleFileInputChange}
                        style={{ display: 'none' }}
                    />
                    {hasPages && (
                        <button type="button" className="link-btn" onClick={clearAll} disabled={loading}>
                            Clear all
                        </button>
                    )}
                </div>
            </div>

            {isPdf && (
                <div className="image-preview pdf-preview-card">
                    <div className="preview-heading">
                        <strong>Selected PDF</strong>
                        <button type="button" className="page-remove" onClick={() => removePage(pages[0].id)} aria-label="Remove PDF"><TrashIcon /></button>
                    </div>
                    <div className="pdf-preview-placeholder">
                        <strong>{pages[0].file.name}</strong>
                        <p>Every page of this PDF will be read, reviewed, and redacted.</p>
                    </div>
                </div>
            )}

            {imageCount > 0 && (
                <div className="page-thumbs">
                    <div className="preview-heading">
                        <strong>{imageCount === 1 ? '1 page' : `${imageCount} pages`}</strong>
                        <span>Tap the trash icon to remove. Add a photo of each page in order.</span>
                    </div>
                    <div className="page-thumb-grid">
                        {pages.filter(p => p.kind === 'image').map((p, index) => (
                            <div className="page-thumb" key={p.id}>
                                <span className="page-thumb-number">{index + 1}</span>
                                <img src={p.url} alt={`Page ${index + 1}`} />
                                <button type="button" className="page-remove" onClick={() => removePage(p.id)} aria-label={`Remove page ${index + 1}`}><TrashIcon /></button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="upload-value-panel" aria-label="What happens after upload">
                <div>
                    <strong>You stay in control</strong>
                    <span>Correct prices, categories, and hospital details before saving.</span>
                </div>
                <div>
                    <strong>Privacy comes before sharing</strong>
                    <span>Check the image and redact personal details before it is public.</span>
                </div>
                <div>
                    <strong>Every upload helps</strong>
                    <span>Bills and estimates make real vet pricing easier to compare.</span>
                </div>
            </div>

            <div className="step-one-actions">
                <button onClick={handleCancelUpload} className="secondary-btn" type="button" disabled={loading}>
                    Cancel
                </button>
                <button onClick={handleNext} className="next-btn" disabled={loading || !hasPages}>
                    {loading ? 'Processing…' : 'Next'}
                </button>
            </div>

            {loading && (
                <div className="processing-overlay" role="status" aria-live="polite">
                    <div className="processing-card">
                        <div className="processing-spinner" aria-hidden="true" />
                        <strong className="processing-status">{progressMessage}</strong>
                        <span className="processing-hint">Nothing is saved or shared yet — you’ll review everything next. Keep this page open; this can take a minute or two.</span>
                        <button type="button" className="secondary-btn" onClick={handleCancelProcessing}>
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Step1Upload;
